import type { GetInvoicesOverviewsResponse } from '@vy/api-definition';
import { GetInvoicesOverviewsResponseSchema } from '@vy/api-definition';
import type { WeftApi } from '@vy/client';
import { vySerialize } from '@vy/utils';
import type { QueryClient, UseQueryOptions } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { useInvoicesPageParams } from '../../../pages/invoices/hooks/use-invoices-page-params';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import type { QueryCreatorType } from '../../../services/query-key/query-key-creator';

type UseInvoicesOverviewsPayload = Parameters<WeftApi['getInvoicesOverviews']>[0];

export function useInvoicesOverviews(
  payload: UseInvoicesOverviewsPayload = {},
  options?: UseQueryOptions<GetInvoicesOverviewsResponse>,
  queryKeyCreator: QueryCreatorType = queryKeys.invoicesOverviewsNew,
) {
  const queryClient = useQueryClient();
  const queryKey = queryKeyCreator(payload);

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetInvoicesOverviewsQueries(queryClient, queryKeyCreator);

      const getInvoicesOverviewsResponse = await api.getInvoicesOverviews(payload, {
        signal,
      });

      return GetInvoicesOverviewsResponseSchema.parse(getInvoicesOverviewsResponse);
    },
    ...options,
  });
}

export function useInvoicesOverviewsFromParams(options?: UseQueryOptions<GetInvoicesOverviewsResponse>) {
  const payload = useInvoicesOverviewsPayloadFromParams();
  const queryClient = useQueryClient();
  const queryKeyCreator = queryKeys.invoicesOverviewsNew;

  return useQuery({
    queryKey: queryKeyCreator(payload),
    queryFn: async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetInvoicesOverviewsQueries(queryClient, queryKeyCreator);

      const getInvoicesOverviewsResponse = await api.getInvoicesOverviews(payload, {
        signal,
      });

      return GetInvoicesOverviewsResponseSchema.parse(getInvoicesOverviewsResponse);
    },
    ...options,
  });
}

export function useInvoicesOverviewsPayloadFromParams(): UseInvoicesOverviewsPayload {
  const {
    selectedWorkflows,
    search,
    selectedBillingStatuses,
    dates,
    page,
    pageSize,
    selectedAdditionalFilters,
    currentSortModel,
  } = useInvoicesPageParams();

  return {
    search,
    dates: vySerialize(dates),
    billingStatuses: vySerialize(selectedBillingStatuses),
    reviewStatuses: vySerialize(selectedWorkflows),
    page,
    pageSize,
    additionalFilters: vySerialize(selectedAdditionalFilters),
    sort: vySerialize(currentSortModel),
  };
}

async function cancelGetInvoicesOverviewsQueries(queryClient: QueryClient, keyCreator: QueryCreatorType) {
  const queryResource = keyCreator()[0];

  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === queryResource,
  });
}

export function useAllInvoicesOverviews(options?: UseQueryOptions<GetInvoicesOverviewsResponse>) {
  const queryClient = useQueryClient();
  const basePayload = useInvoicesOverviewsPayloadFromParams();
  const baseQueryKey = queryKeys.invoicesOverviewsNew(basePayload);

  const lastResponse = queryClient.getQueryData<GetInvoicesOverviewsResponse>(baseQueryKey);

  const payload = {
    ...basePayload,
    pageSize: lastResponse?.count,
  };

  return useInvoicesOverviews(payload, options, queryKeys.allInvoicesOverviews);
}
